import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Tag, AlertCircle, X, Save } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetCampaigns, adminCreateCampaign, adminUpdateCampaign, adminDeleteCampaign, adminGetProducts } from '../../services/api';

const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors placeholder-zinc-600";
const labelCls = "block font-inter text-xs text-zinc-400 uppercase tracking-wider mb-1.5";

function getCampaignStatus(c) {
  const now = new Date();
  const start = new Date(c.start_at);
  const end = new Date(c.end_at);
  if (!c.active) return { label: 'Inactive', cls: 'text-zinc-500 bg-zinc-800 border-zinc-700' };
  if (now < start) return { label: 'Scheduled', cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' };
  if (now > end) return { label: 'Expired', cls: 'text-red-400 bg-red-500/10 border-red-500/20' };
  return { label: 'Active', cls: 'text-green-400 bg-green-500/10 border-green-500/20' };
}

const BLANK = {
  name: '', description: '', discount_type: 'percentage', discount_value: '',
  applies_to: 'all', category_ids: [], product_ids: [],
  start_at: '', end_at: '', active: true,
};

export default function AdminCampaigns() {
  const { token } = useAdminAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    Promise.all([adminGetCampaigns(token), adminGetProducts(token)])
      .then(([c, p]) => { setCampaigns(c); setProducts(p.filter(p => p.status === 'active')); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const openCreate = () => { setForm(BLANK); setEditing(null); setError(''); setModalOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name, description: c.description || '',
      discount_type: c.discount_type, discount_value: String(c.discount_value || ''),
      applies_to: c.applies_to || 'all',
      category_ids: c.category_ids || [],
      product_ids: c.product_ids || [],
      start_at: c.start_at ? c.start_at.slice(0, 16) : '',
      end_at: c.end_at ? c.end_at.slice(0, 16) : '',
      active: c.active !== false,
    });
    setEditing(c._id);
    setError('');
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    await adminDeleteCampaign(token, id);
    setCampaigns(prev => prev.filter(c => c._id !== id));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      if (editing) {
        const updated = await adminUpdateCampaign(token, editing, payload);
        setCampaigns(prev => prev.map(c => c._id === editing ? updated : c));
      } else {
        const created = await adminCreateCampaign(token, payload);
        setCampaigns(prev => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggleProductId = (id) => {
    setForm(f => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter(i => i !== id) : [...f.product_ids, id],
    }));
  };

  return (
    <div data-testid="admin-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">CAMPAIGNS</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Create and manage discount campaigns with auto-expiry</p>
        </div>
        <button
          data-testid="create-campaign-button"
          onClick={openCreate}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <Plus size={16} />New Campaign
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.length === 0 && (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <Tag size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO CAMPAIGNS YET</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Create your first discount campaign above</p>
            </div>
          )}
          {campaigns.map(c => {
            const status = getCampaignStatus(c);
            return (
              <div key={c._id} data-testid={`campaign-row-${c._id}`} className="bg-[#1a1a1a] border border-white/10 p-5 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${status.cls}`}>
                        {status.label}
                      </span>
                      <h3 className="font-bebas text-xl text-white tracking-wider">{c.name}</h3>
                    </div>
                    {c.description && <p className="font-inter text-sm text-zinc-500 mb-2">{c.description}</p>}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="font-inter text-zinc-400">
                        <span className="text-orange-500 font-bold">
                          {c.discount_type === 'percentage' ? `${c.discount_value}% OFF` : `$${c.discount_value} OFF`}
                        </span>
                        {' '}on{' '}
                        <span className="text-white capitalize">{c.applies_to === 'all' ? 'All Products' : c.applies_to}</span>
                      </span>
                      <span className="font-inter text-zinc-500">
                        {new Date(c.start_at).toLocaleDateString()} → {new Date(c.end_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      data-testid={`edit-campaign-${c._id}`}
                      onClick={() => openEdit(c)}
                      className="p-2 text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`delete-campaign-${c._id}`}
                      onClick={() => handleDelete(c._id)}
                      className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Campaign Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-end">
          <div
            data-testid="campaign-modal"
            className="w-full max-w-lg h-full bg-[#111111] border-l border-white/10 flex flex-col overflow-hidden"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <h2 className="font-bebas text-2xl text-white tracking-wider">
                {editing ? 'EDIT CAMPAIGN' : 'NEW CAMPAIGN'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSave} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">
                    <AlertCircle size={14} />{error}
                  </div>
                )}

                <div>
                  <label className={labelCls}>Campaign Name *</label>
                  <input data-testid="campaign-name-input" required className={inputCls} value={form.name} onChange={set('name')} placeholder="Summer Sale" />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <input className={inputCls} value={form.description} onChange={set('description')} placeholder="Optional description..." />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Discount Type</label>
                    <select data-testid="discount-type-select" className={inputCls} value={form.discount_type} onChange={set('discount_type')}>
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Discount Value</label>
                    <input
                      data-testid="discount-value-input"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      className={inputCls}
                      value={form.discount_value}
                      onChange={set('discount_value')}
                      placeholder={form.discount_type === 'percentage' ? '15' : '25.00'}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Applies To</label>
                  <select data-testid="applies-to-select" className={inputCls} value={form.applies_to} onChange={set('applies_to')}>
                    <option value="all">All Products</option>
                    <option value="categories">Specific Categories</option>
                    <option value="products">Specific Products</option>
                  </select>
                </div>

                {form.applies_to === 'categories' && (
                  <div>
                    <label className={labelCls}>Categories</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['suppressors', 'sme', 'clamp-ons', 'muzzle-devices'].map(cat => (
                        <label key={cat} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.category_ids.includes(cat)}
                            onChange={e => setForm(f => ({
                              ...f,
                              category_ids: e.target.checked ? [...f.category_ids, cat] : f.category_ids.filter(c => c !== cat),
                            }))}
                            className="accent-orange-500"
                          />
                          <span className="font-inter text-xs text-zinc-400 capitalize">{cat}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.applies_to === 'products' && (
                  <div>
                    <label className={labelCls}>Select Products</label>
                    <div className="max-h-40 overflow-y-auto bg-[#1e1e1e] border border-white/10">
                      {products.map(p => (
                        <label key={p._id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.product_ids.includes(p.slug)}
                            onChange={() => toggleProductId(p.slug)}
                            className="accent-orange-500"
                          />
                          <span className="font-inter text-xs text-zinc-300">{p.name}</span>
                          <span className="font-inter text-xs text-zinc-600 ml-auto">${p.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Start Date & Time</label>
                    <input
                      data-testid="campaign-start-date"
                      type="datetime-local"
                      required
                      className={inputCls}
                      value={form.start_at}
                      onChange={set('start_at')}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>End Date & Time</label>
                    <input
                      data-testid="campaign-end-date"
                      type="datetime-local"
                      required
                      className={inputCls}
                      value={form.end_at}
                      onChange={set('end_at')}
                    />
                  </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={set('active')}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <span className="font-inter text-sm text-zinc-300">Campaign Active</span>
                </label>
              </div>

              <div className="border-t border-white/10 p-6">
                <button
                  data-testid="save-campaign-button"
                  type="submit"
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-3 transition-colors"
                >
                  <Save size={16} />
                  {saving ? 'SAVING...' : 'SAVE CAMPAIGN'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
